import { useCallback, useState } from 'react';
import { Check, X } from 'lucide-react';

import { formatIcd10CodeForDisplay } from '@/shared/lib/formatters';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';

import { useSearchICD10Query } from '../model/useSearchICD10Query.js';
import type { ICD10CodeT } from '../api/searchICD10.js';

export interface ICD10SingleSelectProps {
  /** The currently chosen code, or ``null`` when nothing is selected. */
  selected: ICD10CodeT | null;
  /** ``true`` while a prefilled code's description is being resolved. */
  isResolving?: boolean;
  onSelect: (code: ICD10CodeT) => void;
  onClear: () => void;
  placeholder?: string;
}

/**
 * A single-select ICD-10 code picker: a debounced search combobox that
 * collapses to a chip once a code is chosen (with a clear affordance).
 *
 * Shared between the diagnose wizard and the referral wizard's inline
 * "new diagnosis" path so both search / display codes identically. Backed by
 * the labs entity's ICD-10 search (`useSearchICD10Query`).
 */
export function ICD10SingleSelect({
  selected,
  isResolving = false,
  onSelect,
  onClear,
  placeholder = 'Search ICD-10 codes…',
}: ICD10SingleSelectProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useSearchICD10Query(search, open);
  const results = data?.codes ?? [];

  const handleSelect = useCallback(
    (code: ICD10CodeT) => {
      onSelect(code);
      setSearch('');
      setOpen(false);
    },
    [onSelect]
  );

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 p-2">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant="secondary" className="font-mono text-xs font-semibold">
            {formatIcd10CodeForDisplay(selected.code)}
          </Badge>
          {selected.description ? (
            <span className="truncate text-sm">{selected.description}</span>
          ) : (
            <span className="truncate text-xs italic text-muted-foreground">
              {isResolving ? 'Resolving description…' : 'Unknown code'}
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-6 shrink-0 p-0"
          onClick={onClear}
          aria-label="Clear selected code"
        >
          <X className="size-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <Command
      className="relative overflow-visible bg-transparent [&_[data-slot=command-input-wrapper]]:rounded-full [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-list]]:absolute [&_[data-slot=command-list]]:inset-x-0 [&_[data-slot=command-list]]:top-full [&_[data-slot=command-list]]:z-10 [&_[data-slot=command-list]]:mt-1 [&_[data-slot=command-list]]:rounded-md [&_[data-slot=command-list]]:border [&_[data-slot=command-list]]:bg-popover [&_[data-slot=command-list]]:shadow-md"
      shouldFilter={false}
    >
      <CommandInput
        placeholder={placeholder}
        value={search}
        onValueChange={(v) => {
          setSearch(v);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setTimeout(() => setOpen(false), 200);
        }}
      />
      {open && search.trim().length >= 2 && (
        <CommandList>
          {isFetching && results.length === 0 && (
            <div className="px-3 py-2 text-center text-xs text-muted-foreground">Searching…</div>
          )}
          {!isFetching && results.length === 0 && (
            <CommandEmpty>No matching codes found.</CommandEmpty>
          )}
          {results.length > 0 && (
            <CommandGroup>
              {results.map((code) => (
                <CommandItem key={code.code} value={code.code} onSelect={() => handleSelect(code)}>
                  <Check className="size-3.5 opacity-0" />
                  <span className="min-w-[60px] font-mono text-xs font-semibold">
                    {formatIcd10CodeForDisplay(code.code)}
                  </span>
                  <span className="truncate text-sm">{code.description}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      )}
    </Command>
  );
}
